(ns app.front.view.group.admin.config
  (:require [app.admin.config.util :as conf-util]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.label-group-state :as label-group-state]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.approval-authority-config :as approval-authority-config]
            [app.front.view.group.admin.archiving-config :as archiving-config]
            [app.front.view.group.admin.attachment-type-config :as at-config]
            [app.front.view.group.admin.case-id-config :as case-id-config]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.complaint-config :as complaint-config]
            [app.front.view.group.admin.condition-due-date-config :as condition-due-date-config]
            [app.front.view.group.admin.conditions.subs :as condition-subs]
            [app.front.view.group.admin.config-selectors :as conf-sel]
            [app.front.view.group.admin.config.app-pc-selectors :as app-pc-selectors]
            [app.front.view.group.admin.config.licence-notifications :as licence-notification-config]
            [app.front.view.group.admin.config.notifications :as notification-config]
            [app.front.view.group.admin.config.number-format-editor :as nf-editor]
            [app.front.view.group.admin.config.pre-con-expiration :as pre-con-expiration-config]
            [app.front.view.group.admin.config.upper-tier-visibility :as upper-tier-visibility]
            [app.front.view.group.admin.config.verdict-defaults-config :as verdict-defaults]
            [app.front.view.group.admin.core :as core]
            [app.front.view.group.admin.express-issuance-config :as express-issuance-config]
            [app.front.view.group.admin.inspections.closure-categories :as closure-categories]
            [app.front.view.group.admin.party-role-config :as pr-config]
            [app.front.view.group.admin.permit-type-config :as group.admin.ptc]
            [app.front.view.group.admin.public-map-config :as public-map-config]
            [app.front.view.group.admin.subs :as gas]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.components :as components]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(def entity-attributes
  "Group-config attributes whose value is (a set of) entities"
  [:group.config/form-schemas
   :group.config/pre-consultation-form-schemas
   :group.config/required-attachments
   :group.config/pre-consultation-required-attachments
   :group.config/review-configs
   :group.config/submitted-review-configs
   :group.config/sign-off-declarations
   :group.config/inspection-visit-result-details
   :group.config/label-groups
   :group.config/general-conditions
   :group.config/default-conditions
   :group.config/required-party-roles
   :group.config/required-party-roles-before-issuance
   :group.config/pre-consultation-required-party-roles
   :group.config/applicable-party-roles
   :group.config/close-without-inspection-categories
   :group.config/hidden-property-attribute-templates])

(def raw-attributes
  "Group-config attributes that are inherited as-is from parent-config to child"
  [:group.config/permit-notifications?
   :group.config/permit-sign-off?
   :group.config/permit-issued-notification-interval
   :group.config/completion-date-notification-interval
   :group.config/municipal-case-id-common-counter?
   :group.config/municipal-case-id-new-number-on-renewal?
   :group.config/municipal-case-id-template
   :group.config/municipal-case-id-counter-digits
   :group.config/municipal-case-id-prefix
   :group.config/municipal-case-id-separator
   :group.config/conditions-in-verdict-template?
   :group.config/licence-renewal-limit
   :group.config/target-times-visible-for-authority-only?
   :group.config/target-times-calculation-starting-day
   :group.config/target-times-business-day-end-time
   :group.config/target-time-manual-inputs?
   :group.config/reset-target-times-on-resubmission?
   :group.config/ecm-integration-container-id
   :group.config/municipal-case-id-rollover-month
   :group.config/municipal-case-id-rollover-day
   :group.config/pre-consultation-expires?
   :group.config/pre-consultation-expiration-value
   :group.config/pre-consultation-expiration-unit
   :group.config/pre-consultation-expiration-enforce-step
   :group.config/condition-due-date-value
   :group.config/condition-due-date-unit
   :group.config/condition-due-date-manual-inputs?
   ;; process-flow is not changed here, but some configs are hidden based on it's value
   :group.config/process-flow
   :group.config/express-issuance?])

(def target-time-attributes
  "Group-config attributes related to target time (technically like raw-attributes but handled differently)"
  [:group.config/target-time-decided-value
   :group.config/target-time-completed-value
   :group.config/target-time-appealed-value
   :group.config/target-time-decided-unit
   :group.config/target-time-completed-unit
   :group.config/target-time-appealed-unit])

(defn integration-config-map [group-config]
  (-> group-config
      :group.config/integration
      (select-keys [:db/id
                    :integration/category
                    :integration/work-type
                    :integration/work-target])))

(defn annotate-value-with-source-config-name
  "Config name here being the name of the original config this selection was inherited from"
  [config-name values]
  (reduce #(assoc %1 %2 config-name) {} values))

(defn select-and-tag-attributes
  "Adds the given attributes from the original config to the new one.
  The values are annotated as a map of value (id) to source config name,
  so we can know where the value was inherited from originally"
  [source-config source-config-name new-config ->value attributes]
  (reduce (fn [new-config attribute]
            (assoc new-config
                   attribute
                   (->> (get source-config attribute)
                        (map ->value)
                        (annotate-value-with-source-config-name source-config-name))))
          new-config
          attributes))

(defn- ->id [x]
  (cond-> x (associative? x) :db/id))

(defn get-role-configs
  "Cleans up the role-configs for the editor (by turning entity refs to ids and discards irrelevant fields)"
  [config]
  (->> config
       :group.config/role-configs
       (remove nil?)
       (map #(-> %
                 (select-keys [:db/id :role.config/form-schema :role.config/party-role])
                 (update :role.config/form-schema ->id)
                 (update :role.config/party-role ->id)))))



(defn get-verdict-defaults [{:group.config/keys [verdict-defaults]}]
  (->> verdict-defaults
       (map #(-> %
                 (select-keys [:db/id
                               :verdict-default-config/applies-to
                               :verdict-default-config/section-id
                               :verdict-default-config.date-input/rule
                               :verdict-default-config.date-input/amount
                               :verdict-default-config.date-input/month
                               :verdict-default-config.date-input/day
                               :verdict-default-config.date-input/years
                               :verdict-default-config.date-input/no-backdating?
                               :verdict-default-config.date-input/reference-date
                               :verdict-default-config/authority-user])
                 (update :verdict-default-config/authority-user ->id)))
       (sort-by config-model/verdict-default-identity)))

(defn tag-config-attributes-with-branch-name
  "Creates a new config where some values are annotated with the source config name (e.g. \"Building Permit\")"
  [config-name config]
  (let [tag-config-attributes (partial select-and-tag-attributes config config-name)]
    (-> {:role-form-schemas                             (get-role-configs config)
         :group.config/verdict-defaults                 (get-verdict-defaults config)
         :group.config/inspection-templates             (->> config
                                                             :group.config/inspection-templates
                                                             (map (comp :db/id :entity/parent))
                                                             (annotate-value-with-source-config-name config-name))
         :group.config/application-inspection-templates (->> config
                                                             :group.config/application-inspection-templates
                                                             (map (comp :db/id :entity/parent))
                                                             (annotate-value-with-source-config-name config-name))
         :group.config/approval-authority               (when-let [aa-id (-> config :group.config/approval-authority :db/id)]
                                                          [aa-id config-name])
         :group.config/integration                      (integration-config-map config)
         :group.config/default-approver-groups          (-> config :group.config/default-approver-groups du/entity-id-set)}
        (tag-config-attributes :db/id entity-attributes)
        (merge (select-keys config raw-attributes))
        (assoc
          :group.config/omit-execution-phase? {config-name (:group.config/omit-execution-phase? config)}
          :group.config/no-review-change-requests {config-name (:group.config/no-review-change-requests config)}
          :group.config/require-sign-off-after-changes-requested? {config-name (:group.config/require-sign-off-after-changes-requested? config)}
          :group.config/no-workspace-location {config-name (:group.config/no-workspace-location config)}
          :group.config/hide-from-public-map {config-name (:group.config/hide-from-public-map config)}
          :group.config/public-map-visible-cr {config-name (:group.config/public-map-visible-cr config)}
          :group.config/public-map-visible-licence {config-name (:group.config/public-map-visible-licence config)}
          :group.config/add-complainant-as-related-party? {config-name (:group.config/add-complainant-as-related-party? config)}
          :group.config/initial-case-id-counter {config-name (:group.config/initial-case-id-counter config)}
          :group.config/licence-renewal-limit {config-name (:group.config/licence-renewal-limit config)}
          :group.config/licence-notifications {config-name (licence-notification-config/config->values config)}
          :group.config/pre-consultation-expiration-notifications {config-name (notification-config/config->values
                                                                                 :group.config/pre-consultation-expiration-notifications
                                                                                 config)}
          :group.config/bond-required? {config-name (:group.config/bond-required? config)}
          :group.config/express-issuance-template {config-name (:group.config/express-issuance-template config)}))))

(defn make-new-parent-config
  "A parent config has its values annotated with the name of their source config"
  [config-name config previous-parent-config]
  (merge (->> config
              (tag-config-attributes-with-branch-name config-name)
              (p/deep-merge :into previous-parent-config))
         (when-some [schema-id (-> config :group.config/application-sign-off-schema :db/id)]
           {:application-sign-off-schema [schema-id config-name]})))

(defn select-defaults
  "Like select-keys but applies the function f to the values and includes keys
  even if they were left out of the original config"
  [config f ks]
  (reduce (fn [m k]
            (assoc m k (->> config k f)))
          {}
          ks))

(defn default-atom
  "Create a temporary config data map. Note that you need empty sets in the output
  even when the underlying data has missing keys, so select-keys does not cut it."
  [config]
  (let [select-defaults (partial select-defaults config)]
    (merge
      {:group.config/role-configs                              (or (get-role-configs config) [])
       :group.config/verdict-defaults                          (get-verdict-defaults config)
       :group.config/integration                               (integration-config-map config)
       :group.config/licence-notifications                     (licence-notification-config/config->values config)
       :group.config/pre-consultation-expiration-notifications (notification-config/config->values
                                                                 :group.config/pre-consultation-expiration-notifications
                                                                 config)
       :group.config/initial-case-id-counter                   (dec (nf-editor/counter-value
                                                                      {:entity                  config
                                                                       :entity-id               (:db/id config)
                                                                       :group                   (:group.config/group config)
                                                                       :common-attr             :group.config/municipal-case-id-common-counter?
                                                                       :counter-attr            :group.config/initial-case-id-counter
                                                                       :year-attr               :group/current-application-id-year
                                                                       :rollover-attr           :group.config/municipal-case-id-rollover
                                                                       :rollover-month          (:group.config/municipal-case-id-rollover-month config)
                                                                       :rollover-day            (:group.config/municipal-case-id-rollover-day config)
                                                                       :ignore-unsaved-changes? true}))}
      (select-defaults :db/id [:group.config/application-sign-off-schema
                               :group.config/approval-authority
                               :group.config/express-issuance-template])
      (select-defaults identity (concat [:group.config/omit-execution-phase?
                                         :group.config/no-review-change-requests
                                         :group.config/require-sign-off-after-changes-requested?
                                         :group.config/no-workspace-location
                                         :group.config/hide-from-public-map
                                         :group.config/public-map-visible-cr
                                         :group.config/public-map-visible-licence
                                         :group.config/licence-renewal-limit
                                         :group.config/grace-period
                                         :group.config/bond-required?
                                         :group.config/add-complainant-as-related-party?
                                         :group.config/express-issuance?]
                                        raw-attributes
                                        target-time-attributes))
      (select-defaults set [:group.config/supplemental-permits])
      (select-defaults du/entity-id-set (concat [:group.config/default-approver-groups
                                                 :group.config/inspection-templates
                                                 :group.config/application-inspection-templates
                                                 :group.config/hidden-property-attribute-templates]
                                                entity-attributes)))))

(defn- role-selection-valid? [group-config parent-config]
  (and (every? (fn [{:role.config/keys [form-schema party-role]}]
                 (and (not= form-schema :none)
                      (int? party-role)))
               (:group.config/role-configs group-config))
       (or (nil? (:group.config/application-sign-off-schema group-config))
           (let [declaration-ids      (set
                                        (concat (:group.config/sign-off-declarations group-config)
                                                (-> parent-config
                                                    :group.config/sign-off-declarations
                                                    set
                                                    keys)))
                 required-party-roles (<sub [::conf-sel/party-role-ids-for-declaration-ids declaration-ids])
                 enabled-role-ids     (set/union (:group.config/applicable-party-roles group-config)
                                                 (-> parent-config
                                                     :group.config/applicable-party-roles
                                                     keys
                                                     set))]
             (and (seq declaration-ids)
                  (every? #(contains? enabled-role-ids %) required-party-roles))))))

(defn target-time-unit-defined-if-value-defined? [m [k-unit k-value]]
  (or (-> m k-value nil?)
      (-> m k-unit some?)))

(def related-keys
  [[:group.config/target-time-appealed-unit :group.config/target-time-appealed-value]
   [:group.config/target-time-completed-unit :group.config/target-time-completed-value]
   [:group.config/target-time-decided-unit :group.config/target-time-decided-value]])

(defn- target-times-valid? [group-config permit-type]
  (let [related-keys (if (ptc-model/building-permit-types permit-type)
                       (drop 1 related-keys)
                       related-keys)]
    (or (not (<sub [::gas/has-feature? :group.feature/target-processing-times]))
        (if (-> group-config :group.config/target-time-manual-inputs?)
          (->> related-keys
               (every? #(some? (-> group-config (get (first %))))))
          (->> related-keys
               (every? #(and (target-time-unit-defined-if-value-defined? group-config %)
                             (-> group-config (get (last %)) neg-int? not))))))))

(defn config-valid? [group-config parent-config permit-type]
  (and (role-selection-valid? group-config parent-config)
       (verdict-defaults/verdict-defaults-valid? group-config)
       (target-times-valid? group-config permit-type)
       (licence-notification-config/valid? group-config parent-config)
       (notification-config/valid-configs? :group.config/pre-consultation-expiration-notifications group-config)
       (conf-util/prefix-valid? group-config
                                (<sub [::gas/group])
                                :group.config/municipal-case-id-prefix
                                :group.config/municipal-case-id-common-counter?)
       (pre-con-expiration-config/pre-con-expiration-valid? group-config)
       (condition-due-date-config/valid? group-config)
       (express-issuance-config/valid? group-config parent-config permit-type)))

(defn remove-ids [entities]
  (->> entities
       (map #(dissoc % :db/id))))

(defn config=
  "Compare the config content of two config-atoms. Configs may contain temporary ids, don't consider those
  for determining difference."
  [& configs]
  (->> configs
       (map (fn [config]
              (-> config
                  (update :group.config/role-configs remove-ids))))
       (apply =)))

(defn action-buttons [{:keys [tr config *group-config group-id config-id parent-config permit-type]}]
  (let [original-config (default-atom config)
        processing?     (<sub [::core/saving-config?])]
    (when-not (config= original-config @*group-config)
      [ui-action-buttons/save-cancel-buttons
       {:on-cancel      #(do (rf/dispatch-sync [::nf-editor/undo-changes config-id])
                             ;; Must reform default-atom instead of using `original-config` here,
                             ;; since it would cause the counter state to un-synchronize
                             (reset! *group-config (default-atom config)))
        :on-save        #(do (>evt [::core/save-group-config
                                    group-id
                                    config-id
                                    @*group-config
                                    (fn [resp]
                                      [::case-id-config/check-group-config-counter-coercion
                                       original-config
                                       *group-config
                                       resp])])
                             (>evt [::nf-editor/undo-changes config-id]))
        :save-disabled? (not (config-valid? (assoc @*group-config :db/id config-id)
                                            parent-config
                                            permit-type))
        :loading?       processing?
        :size           :small
        :tr             tr}])))


(defn group-config-editor
  [{:keys [config name-chain parent-config permit-type authority-group group-id superdomain-id]
    :or   {name-chain    []
           parent-config {}}}]
  (r/with-let [*group-config (r/atom (default-atom config))]
    (let [tr                (<sub [::gas/group-tr])
          lang              (<sub [:language])
          config-id         (:db/id config)
          config-name       (config-model/localized-name config lang)
          name-chain        (->> config-name
                                 (conj name-chain))
          new-parent-config (make-new-parent-config config-name config parent-config)
          editor-ctx        (util/mmap {:target config-id
                                        :level  (:group.config/level config)}
                                       tr *group-config parent-config permit-type name-chain group-id superdomain-id)]
      [:div {:key (str config-id "-config-leaf")}
       [:div (tr [:group-admin/config-leaf-title]
                 [(str/join " / " name-chain)])]
       [:form.admin-config-form {}
        [app-pc-selectors/selectors editor-ctx]
        [action-buttons (util/mmap tr config *group-config config-id group-id parent-config permit-type)]]
       (when-let [children (seq (<sub [::gas/child-group-configs-sorted config-id]))]
         (cc/accordion {}
                       (for-frag [child-config children
                                  :let [child-id (:db/id child-config)]]
                         [components/accordion-item
                          child-id
                          (config-model/localized-name child-config lang)
                          ^{:key (str child-id "-editor-" (:entity/version child-config -1))}
                          [group-config-editor (util/mmap {:config        child-config
                                                           :parent-config new-parent-config}
                                                          name-chain
                                                          group-id
                                                          superdomain-id
                                                          authority-group
                                                          permit-type)]])))])))


(defn- code-enforcement? [root-configs]
  (and (-> root-configs first :group.config/target (= :permit-type/CE))
       (-> root-configs rest empty?)))


(defn- section-options [collapsible? section-id]
  {:section-id         section-id
   :collapsible?       collapsible?
   :default-collapsed? true})


(defn view [match]
  (let [tr                 (<sub [::gas/group-tr])
        group-id           (-> match :parameters :path :group-id)
        superdomain-id     (<sub [::gas/superdomain-id])
        authority-group    (<sub [::entities/entity group-id])
        root-configs       (<sub [::gas/root-group-configs])
        permit-types       (keep :group.config/target root-configs)
        code-enforcement?  (code-enforcement? root-configs)
        collapsible?       (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
        section-options    (partial section-options collapsible?)
        integration?       (<sub [::gas/has-feature? :group.feature/backend-system-integration])
        archiving-feature? (contains? (<sub [::gas/group-features]) :group.feature/archiving)
        pending-configs    (<sub [::core/pending-config-fetches group-id [:group/configuration
                                                                          :group/permit-type-configs
                                                                          :group/attachment-types
                                                                          :group/complaint-sources
                                                                          :approval/stakeholder-groups
                                                                          :group/condition-templates]])]
    [acommon/admin-page
     {:match match
      :title (tr [:group-admin/config-title])}
     (cond
       (seq pending-configs)
       [:section
        (cc/inline-loading {:description (tr [:loading])})]

       (empty? root-configs)
       [:section
        [:h5.mb-2 (tr [:error/not-found])]]

       :else
       [:<>
        ^{:key (str group-id "-permit-type-configs")}
        [group.admin.ptc/permit-type-configs-section tr group-id (section-options "permit-types")]
        ^{:key (str group-id "-complaints")}
        [complaint-config/complaint-config-section (section-options "complaints")]
        ^{:key (str group-id "-public-map")}
        [public-map-config/public-map-config-section permit-types (section-options "public-map")]
        ^{:key (str group-id "-archiving")}
        (when archiving-feature?
          [acommon/config-section
           (tr [:group-admin/archiving])
           [:div.admin-page__instruction.bx--col-bx-12
            [:span (tr [:group-admin/archiving-instruction])]
            [:div
             [:span.bold (tr [:note])]
             [:span " " (tr [:group-admin/archiving-note])]]]
           (section-options "archiving")
           [archiving-config/archiving-enabled-config tr root-configs]])
        ^{:key (str group-id "-attachment-types")}
        [acommon/config-section
         (tr [:group-admin/attachments-title])
         [:p.admin-page__instruction.bx--col-bx-12.whitespace-pre-line (tr [:group-admin/attachment-instruction])]
         (section-options "attachment-types")
         [at-config/attachment-editors tr root-configs group-id {:save-fn                  core/do-save-group-config
                                                                 :available-attachments-kw :group.config/available-attachments
                                                                 :target-kw                :group.config/target
                                                                 :group-kw                 :group.config/group
                                                                 :kind                     :attachment-type.kind/workspace}]]
        ^{:key (str group-id "-party-roles")}
        [pr-config/party-role-config-section group-id (section-options "party-roles")]
        (when (or (some ptc-model/permit-types-with-closure-categories permit-types)
                  (<sub [::ff/enabled? :feature-flag.id/show-closure-categories]))
          ^{:key (str group-id "-closure-categories")}
          [closure-categories/closure-categories-config-section group-id (section-options "closure-categories")])
        ^{:key (str group-id "-group-configs")}
        [acommon/config-section
         (tr [(if collapsible?
                :group-admin/workspace-requirements-title
                :group-admin/form-config-title)])
         (when-not code-enforcement? (tr [:group-admin/config-instruction]))
         (section-options "workspace-requirements")
         (cc/accordion
           {}
           (for-frag [root-config root-configs]
             (let [permit-type (:group.config/target root-config)]
               [components/accordion-item
                (:db/id root-config)
                (tr [permit-type])
                ^{:key (str (:db/id root-config) "-editor")}
                [group-config-editor (util/mmap {:config root-config
                                                 :path   [(:db/id root-config)]}
                                                permit-type
                                                group-id
                                                superdomain-id
                                                authority-group)]])))]
        [case-id-config/view group-id integration? (section-options "municipal-case-id")]
        [upper-tier-visibility/view group-id (section-options "upper-tier-visibility")]])]))


(def route
  {:name        :view/group.admin.config
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::verdict-defaults/fetch-configurable-sections group-id])
                                  (>evt [::verdict-defaults/fetch-authority-user-default-candidates group-id])
                                  (>evt [::gas/fetch-attachment-types-in-use group-id])
                                  (>evt [::gas/fetch-party-roles group-id])
                                  (>evt [::gas/fetch-closure-categories group-id])
                                  (>evt [::gas/fetch-fee-schedules group-id])
                                  (>evt [::complaint-config/fetch-complaint-sources group-id])
                                  (>evt [::approval-authority-config/fetch-stakeholder-groups group-id])
                                  (>evt [::condition-subs/fetch-templates group-id])
                                  (>evt [::gas/fetch-additional-config group-id])
                                  (>evt [::gas/fetch-phase-states group-id])
                                  (>evt [::label-group-state/fetch-label-groups group-id])))
                  :stop       (fn []
                                (>evt [::group.admin.ptc/cleanup])
                                (>evt [::complaint-config/cleanup])
                                (>evt [::gas/clean-additional-config]))}]})
